import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

// Components
import { Button, Switch, Text } from '@beef/ui-kit';
import { I18n, Icon } from 'components';
import { Cards } from 'components/Cards';
import { Sliders } from '../Sliders';
import { DeleteButton } from '../DeleteButton';

// Utils
import { getAccumulatorNames } from '../../../../utils';

// Styles
import styles from './styles.pcss';

const cn = classNames.bind(styles);

const AvailableSetupLimit = ({
  accumulators = [],
  handleDeleteBtnClick,
  handleDetailsBtnClick,
  handleSaveLimitsBtnClick,
  isProcessing,
  ctn,
  isShowLimitText,
  accessStatusText,
}) => {
  const [isLimitsChanged, setIsLimitChanged] = useState(false);
  const [isMaximumLimits, setIsMaximum] = useState(false);
  const [settledLimits, setSettledLimits] = useState(() => getAccumulatorNames(accumulators));
  const [isSwitchChecked, setSwitchStatus] = useState(false);
  const [isShowStatus, setIsShowStatus] = useState(false);

  useEffect(() => {
    const isChanged = accumulators?.some((elem) => settledLimits[elem.name] !== elem.limit);
    setIsLimitChanged(isChanged);
  }, [settledLimits]);

  useEffect(() => {
    const isMaximum = accumulators?.every((elem) => elem.parentLimit === settledLimits[elem.name]);
    setIsMaximum(isMaximum);
  }, [settledLimits]);

  useEffect(() => {
    setSwitchStatus(isMaximumLimits);
  }, [isMaximumLimits]);

  useEffect(() => {
    // eslint-disable-next-line arrow-body-style
    const exceedsParentBalance = accumulators.some((acc) => {
      return (acc.parentBalance < settledLimits[acc.name]) && !isMaximumLimits && !isProcessing;
    });
    setIsShowStatus(exceedsParentBalance);
  }, [settledLimits, isMaximumLimits]);

  const handleSwitchChange = useCallback(() => setSwitchStatus((prev) => !prev), []);

  const handleSaveLimits = useCallback(() => {
    handleSaveLimitsBtnClick(settledLimits, ctn, isMaximumLimits);
  }, [settledLimits, isMaximumLimits]);

  return (
    <>
      {!!accumulators?.length && (
        <>
          <div className={cn('cards')}>
            <Cards accumulators={accumulators} isProcessing={isProcessing} />

            {isShowLimitText && (
            <div className={cn('cards__status-limit')}>
              <Text
                color="grey50"
                size="size5-r-s"
              >
                {accessStatusText}
              </Text>
            </div>
            )}
          </div>
        </>
      )}

      {!!accumulators?.length && !isProcessing && (
        <div className={cn('setups')}>
          <Sliders
            accumulators={accumulators}
            isProcessing={isProcessing}
            isSwitchChecked={isSwitchChecked}
            settledLimitsFunc={setSettledLimits}
            isShowStatus={isShowStatus}
          />
        </div>
      )}
      {handleDetailsBtnClick && handleDeleteBtnClick && (
        <div className={cn('unit')}>
          <div className={cn('unit__share')}>
            <Text size="size5-r-s">
              <I18n path="family.ctnSetupTexts.shareAllText" />
            </Text>
            <Switch
              checked={isSwitchChecked}
              onChange={handleSwitchChange}
              disabled={isSwitchChecked || isProcessing}
            />
          </div>
          <div className={cn('unit__details-btn')}>
            <Button variant="plain" onClick={handleDetailsBtnClick}>
              <Text size="size5-m">
                <I18n path="family.ctnSetupTexts.tariffDetailsText" />
              </Text>
              <Icon name="arrow" className={cn('unit__details-icon')} />
            </Button>
          </div>
          <div className={cn('unit__remove-btn')}>
            <DeleteButton handleDeleteBtnClick={handleDeleteBtnClick} />
          </div>
          <Button
            variant="secondary"
            size="m"
            disabled={!isLimitsChanged}
            onClick={handleSaveLimits}
          >
            <I18n path="family.ctnSetupTexts.saveBtnText" />
          </Button>
        </div>
      )}
    </>
  );
};

AvailableSetupLimit.propTypes = {
  accumulators: PropTypes.array,
  handleDeleteBtnClick: PropTypes.func,
  handleDetailsBtnClick: PropTypes.func,
  isProcessing: PropTypes.bool,
  handleSaveLimitsBtnClick: PropTypes.func,
  ctn: PropTypes.string,
  isShowLimitText: PropTypes.bool,
  accessStatusText: PropTypes.string,
};

export { AvailableSetupLimit };
